import React, { useEffect, useState } from 'react';
import api from '../../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Loader2, Check, RotateCcw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from './demoConstants';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function InvoiceOverrideDialog({ demoId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pick, setPick] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!demoId) return;
    setLoading(true);
    api.get(`/training/demos/reports/invoice-candidates/${demoId}`)
      .then((r) => { setData(r.data); setPick(r.data.override?.invoice_id || ''); setReason(r.data.override?.reason || ''); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load the invoices'))
      .finally(() => setLoading(false));
  }, [demoId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/training/demos/reports/invoice-override/${demoId}`, { invoice_id: pick, reason });
      toast.success('Invoice pinned to this demo');
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the override'); }
    finally { setBusy(false); }
  };

  const revert = async () => {
    setBusy(true);
    try {
      await api.post(`/training/demos/reports/invoice-override/${demoId}`, { invoice_id: null });
      toast.success('Back to the automatic pick');
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not revert'); }
    finally { setBusy(false); }
  };

  const list = data?.invoices || [];

  return (
    <Dialog open={!!demoId} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col bg-white" data-testid="invoice-override-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Pick the admission invoice</DialogTitle>
          <DialogDescription className="text-[11.5px] text-slate-500">
            {data ? <>{data.student_name} · demo {data.demo_code} on {fmtDate(data.demo_date)}. The automatic pick is the earliest non-void invoice on/after the demo date, else one up to 15 days before it — choose another only when Books has it wrong.</> : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading invoices…</div>
        ) : (
          <>
            <div className="flex-1 overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                    <th className="px-3 py-2.5 w-8"></th>
                    <th className="px-3 py-2.5">Invoice</th>
                    <th className="px-3 py-2.5">Date</th>
                    <th className="px-3 py-2.5 text-right">Excl. GST</th>
                    <th className="px-3 py-2.5 text-right">Incl. GST</th>
                    <th className="px-3 py-2.5 text-right">Pending</th>
                    <th className="px-3 py-2.5">Program</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((i) => (
                    <tr key={i.invoice_id} onClick={() => setPick(i.invoice_id)}
                      className={`border-b border-slate-100 last:border-0 cursor-pointer transition-colors ${pick === i.invoice_id ? 'bg-violet-50' : 'hover:bg-slate-50'}`}
                      data-testid={`override-invoice-${i.invoice_id}`}>
                      <td className="px-3 py-2.5">
                        <span className={`w-3.5 h-3.5 rounded-full border inline-flex items-center justify-center ${pick === i.invoice_id ? 'border-violet-600 bg-violet-600' : 'border-slate-300'}`}>
                          {pick === i.invoice_id ? <Check className="w-2.5 h-2.5 text-white" /> : null}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="font-mono text-[11px] text-slate-800">{i.invoice_number}</span>
                        <span className="block text-[10px] uppercase tracking-wider text-slate-400">{i.status}</span>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">
                        {fmtDate(i.date)}
                        {i.is_auto ? <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"><Sparkles className="w-2.5 h-2.5" />auto</span> : null}
                        {i.before_demo ? <span className="block text-[10px] text-amber-700">before the demo</span> : null}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900">{inr(i.amount_ex_gst)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{inr(i.amount)}</td>
                      <td className={`px-3 py-2.5 text-right tabular-nums ${i.balance > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{i.balance > 0 ? inr(i.balance) : '—'}</td>
                      <td className="px-3 py-2.5 text-slate-600">{i.program || '—'}</td>
                    </tr>
                  ))}
                  {list.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-10 text-center text-[12px] text-slate-400" data-testid="override-empty">No live invoices found for this student.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="space-y-1.5 pt-1">
              <Label className="text-[12px] text-slate-600">Why this invoice? (recorded on the demo's audit trail)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. first invoice was a registration fee, this is the course invoice"
                className="h-9 text-[13px]" data-testid="override-reason" />
            </div>

            <div className="flex items-center gap-2 pt-1">
              {data?.override?.invoice_id && (
                <Button variant="outline" size="sm" className="h-9" onClick={revert} disabled={busy} data-testid="override-revert">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Use the automatic pick
                </Button>
              )}
              <Button size="sm" className="h-9 ml-auto bg-violet-600 hover:bg-violet-700 text-white"
                onClick={save} disabled={busy || !pick || reason.trim().length < 5} data-testid="override-save">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}Pin this invoice
              </Button>
            </div>
            {data?.override?.invoice_id && (
              <p className="text-[11px] text-slate-500" data-testid="override-current">
                Currently pinned: <b>{data.override.invoice_number}</b> by {data.override.by} · {String(data.override.at || '').slice(0, 10)}
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
